import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import SEO from './SEO';
import './LegalPages.css';
import './Unsubscribe.css';

function Unsubscribe() {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot — bots fill this hidden field, humans don't
    if (honeypot) return;

    // Prevent double submit
    if (submitted) return;

    if (!email) {
      setStatus('error');
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    setSubmitted(true);

    try {
      const webhookUrl = process.env.REACT_APP_UNSUBSCRIBE_WEBHOOK_URL;

      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'newsletter_unsubscribe',
            email: email,
            timestamp: new Date().toISOString(),
          }),
        });
      }

      setStatus('success');
      setEmail('');
    } catch (error) {
      // Still show success to the user — don't leak webhook failures
      setStatus('success');
      setEmail('');
    }
  };

  return (
    <div className="legal-page">
      <SEO
        title="Unsubscribe — UBOTIKA"
        description="Unsubscribe from the UBOTIKA mailing list. Enter your email to stop receiving newsletter messages."
        path="/unsubscribe"
        noindex
      />
      <main id="main-content" className="legal-content unsubscribe-page">
        <h1>Unsubscribe</h1>
        <p className="unsubscribe-subtitle">
          We're sorry to see you go. Enter your email below to unsubscribe from our mailing list.
        </p>
        <p>
          This page only removes you from UBOTIKA newsletter and marketing emails. It does not
          cancel an active service plan or change how your AI receptionist answers business calls.
        </p>

        {status === 'success' ? (
          <div className="unsubscribe-success">
            <FaCheckCircle className="unsubscribe-success-icon" />
            <h2>You've been unsubscribed</h2>
            <p>
              Your email has been removed from our mailing list. You will no longer receive
              emails from us.
            </p>
            <p className="unsubscribe-note">
              Changed your mind? You can always re-subscribe from our{' '}
              <Link to="/#newsletter">newsletter section</Link>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="unsubscribe-form">
            {/* Honeypot field — hidden from humans, bots fill it */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="unsubscribe-honeypot"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <div className="unsubscribe-input-group">
              <div className="unsubscribe-input-wrapper">
                <FaEnvelope className="unsubscribe-input-icon" />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error') {
                      setStatus('idle');
                      setErrorMessage('');
                    }
                  }}
                  className={`unsubscribe-input ${status === 'error' ? 'error' : ''}`}
                  disabled={status === 'loading'}
                  aria-label="Email address"
                />
              </div>
              {status === 'error' && (
                <span className="unsubscribe-error">
                  <FaExclamationTriangle /> {errorMessage}
                </span>
              )}
            </div>
            <button
              type="submit"
              className="unsubscribe-button"
              disabled={status === 'loading' || submitted}
            >
              {status === 'loading' ? (
                <span className="unsubscribe-spinner"></span>
              ) : (
                'Unsubscribe'
              )}
            </button>
          </form>
        )}

        <div className="legal-back-link">
          <Link to="/">Back to Home</Link>
        </div>
      </main>
    </div>
  );
}

export default Unsubscribe;
